import gsap from 'gsap'
import Noise from 'simplex-noise'
import { Component } from 'shimmer'
import SmartObject from '@/webGL/objects/SmartObject'

import { Loader } from '@/utils/Loader'

const noiseAmp = 2
const noiseHalfAmp = noiseAmp * .5

export class BaseObject extends Component {
  constructor ({ url, index = 0, isFloating = true, rotate = '180', targetScale = 1 }) {
    super('BaseObject')

    this.promise = new Promise(resolve => {this.resolve = resolve})

    this._isVisible = false
    this.size = 0
    this.opacity = 0
    this.index = index
    this.isFloating = isFloating
    this.rotate = rotate

    this.object = new SmartObject(Loader.load)
    this.url = url
    this.noise = new Noise()
    this._basePosition = this.position.clone()

    this.object.load(url).then(() => {
      this.object.scale.set(10, 10, 10)
      this.object.scale.multiplyScalar(targetScale)
      this.resolve()
    })

    if (this.rotate === '180') {
      this.autoRotate()
    }

    this.add(this.object)
  }

  onUpdate ({ elapsed }) {
    if (this.isVisible) {
      if (this.isFloating) {
        this.position.x = this._basePosition.x + this.noise.noise2D(this.index + 0, elapsed * .0002) * noiseAmp - noiseHalfAmp
        this.position.y = this._basePosition.y + this.noise.noise2D(this.index + 1, elapsed * .0002) * noiseAmp - noiseHalfAmp
        this.position.z = this._basePosition.z + this.noise.noise2D(this.index + 2, elapsed * .0002) * noiseAmp - noiseHalfAmp
      }
      if (this.rotate === '360') {
        this.rotation.y += .01
      }
    }
  }

  set basePosition (value) {
    this._basePosition = value
    this.position.copy(this._basePosition)
  }

  get basePosition () {
    return this._basePosition
  }

  set isVisible (value) {
    this._isVisible = value

    if (this._isVisible) {
      gsap.fromTo(this, {
        size: 0
      },{
        size: 1,
        duration: 1.5,
        ease: 'Bounce.easeOut',
        onComplete: () => {
          this.matrixWorldNeedsUpdate = true
        }
      })

      gsap.fromTo(this, {
        opacity: 0
      },{
        opacity: 1,
        duration: .2,
        ease: 'linear'
      })
    }
    else {
      gsap.fromTo(this, {
        size: 1
      },{
        size: 0,
        duration: .5,
        ease: 'power2.in',
        onComplete: () => {
          this.matrixWorldNeedsUpdate = true
        }
      })

      gsap.fromTo(this, {
        opacity: 1
      },{
        opacity: 0,
        duration: .2,
        delay: .3,
        ease: 'linear'
      })
    }
  }

  autoRotate () {
    gsap.fromTo(this.rotation, {
      y: -Math.PI * .5
    },{
      y: Math.PI * .5,
      duration: 2,
      ease: 'power3.inOut',
      yoyo: true,
      repeat: -1
    })
  }

  get isVisible () {
    return this._isVisible
  }

  set size (value) {
    this._scale = value
    this.scale.set(this._scale, this._scale, this._scale)
  }

  get size () {
    return this._scale
  }
}
